import React, {useMemo, useState} from "react";
import {Institute} from "fearless-common";
import {backendEndpoints} from "../libs/backendEndpoints";
import {Badge, Tooltip} from "react-bootstrap";
import useGet from "./hooks/useGet";
import {Loading, OptionalOverlayTrigger} from "fearless-common-ui";
import axios from "axios";
import {toast} from "react-hot-toast";

export interface GroupInstitutePivot {
  institute_id: number;
  group_id: number;
  status: 'pending' | 'active' | 'declined';
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

const tooltipDescriptions = {
  pending: 'Pending - the institute admin has to approve you joining their institute. After approval, you will be able to see institute resources (protocols, courses, .etc)',
  active: '',
  declined: 'This means the institute admin declined your request to join the institute.',
};

/**
 * Response from `GET /me/institute-request`. The backend returns at most one of
 * the two keys, never both:
 * - `suggestion`: an institute the user is eligible to join but has not yet
 *   requested. The UI offers a "Request to join" button.
 * - `request`: an institute the user has already requested to join, carrying the
 *   pivot row whose `status` drives the badge and tooltip.
 */
type RespData = {
  suggestion: Institute;
  request: Institute & { pivot: GroupInstitutePivot }
}

/**
 * Inline control for a user's relationship to a (suggested) institute's shared
 * resources. Fetches the user's institute-request state and renders one of:
 * - a "Request to join" button when an institute is suggested,
 * - a status badge (pending / active / declined) plus a "cancel" button when a
 *   request already exists,
 * - nothing when neither is present.
 *
 * @param userId The user whose institute-request state is fetched and mutated.
 */
export default function InstituteRequestOption({ userId }: { userId: number }) {
  const { data, isLoading, reload } = useGet<RespData>({ endpoint: backendEndpoints.instituteRequest, params: { user_id: userId } });
  const [posting, setPosting] = useState(false);

  const status = data?.request?.pivot.status;
  const tooltip = useMemo(() => (status ? tooltipDescriptions[status] : ''), [status]);

  if (isLoading) return <Loading />;
  if (data?.suggestion) {
    return (
      <div className="d-flex align-items-center gap-1 text-sm">
        <button
          className="btn btn-primary btn-sm px-3 py-1 m-0"
          disabled={posting}
          onClick={() => {
            setPosting(true)
            axios
              .post(backendEndpoints.instituteRequest, { user_id: userId, institute_id: data?.suggestion?.id })
              .catch((e) => {
                toast.error(e.response?.data?.message);
              }).finally(() => {
                reload();
                setPosting(false);
              });
          }}
          style={{ width: '130px' }}
        >
          {posting ? <Loading /> : 'Request to join'}
        </button>
        &nbsp;the shared resources of {data.suggestion.name}.
      </div>
    );
  }

  return (
    <>
      {data?.request && (
        <div className="d-flex align-items-center gap-2">
          <OptionalOverlayTrigger
            overlay={tooltip ? <Tooltip id="request-tooltip">{tooltip}</Tooltip> : undefined}
          >
            <Badge>
              {data?.request?.pivot.status}
            </Badge>
          </OptionalOverlayTrigger>
          <small className="m-0 p-0">
            request to join the shared resources.
          </small>
          <button
            className="btn btn-link p-0 text-danger m-0"
            onClick={() => {
              axios.delete(backendEndpoints.instituteRequest, { data: { institute_id: data.request.id } })
                .catch((e) => {
                  toast.error(e.response?.data?.message);
                }).finally(() => {
                  reload();
                });
            }}
          >
            cancel
          </button>
        </div>
      )}
    </>
  );
}

